import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// Admin-only: drive the Order.status workflow state machine.
//
// Server validates the transition against the table in
// backend/src/routes/orders.ts. Bad transitions return 409.
//
// Mirrors useReleaseOrder() in adminMutations.ts (which covers the
// HOLD -> PRODUCTION transition); everything else flows through here.

export type OrderStatusLower =
  | "draft"
  | "proof"
  | "hold"
  | "production"
  | "factory_received"
  | "packed"
  | "shipped"
  | "delivered"
  | "cancelled";

const TO_UPPER: Record<OrderStatusLower, string> = {
  draft: "DRAFT",
  proof: "PROOF",
  hold: "HOLD",
  production: "PRODUCTION",
  factory_received: "FACTORY_RECEIVED",
  packed: "PACKED",
  shipped: "SHIPPED",
  delivered: "DELIVERED",
  cancelled: "CANCELLED",
};

export type TransitionOrderArgs = {
  orderId: string;
  to: OrderStatusLower;
  reason?: string;
};

export function useTransitionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, to, reason }: TransitionOrderArgs) =>
      api<{ ok: true; status: OrderStatusLower; royalMailTracking: string | null }>(
        `/api/v1/admin/orders/${orderId}/status`,
        {
          method: "POST",
          body: { to: TO_UPPER[to], reason },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "orders"] });
      qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}
